import { useState } from 'react';
import type { SavedFlight } from '@shared/types';
import { useSavedFlightStatus } from '@/hooks/useSavedFlightStatus';
import { formatTimeLabel } from '@/lib/formatting';
import { PlaneStatusIcon } from './Icons';

function buildIcs(saved: SavedFlight): string {
  const dtStart = saved.scheduled_departure ? new Date(saved.scheduled_departure) : new Date();
  const dtEnd = saved.scheduled_arrival ? new Date(saved.scheduled_arrival) : new Date(dtStart.getTime() + 3 * 3600_000);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    `UID:${saved.id}@babcocknav.com`,
    `DTSTART:${fmt(dtStart)}`,
    `DTEND:${fmt(dtEnd)}`,
    `SUMMARY:${saved.ident} ${saved.origin} to ${saved.destination}`,
    `DESCRIPTION:Flight ${saved.ident} from ${saved.origin} to ${saved.destination}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

export function SavedFlightStatusCard({ saved, onRemove }: { saved: SavedFlight; onRemove: () => void }) {
  const status = useSavedFlightStatus(saved.id);
  const [copied, setCopied] = useState(false);
  const flight = status?.flight;

  async function handleShare() {
    const text = `${saved.ident}: ${saved.origin} → ${saved.destination}, ${formatTimeLabel(saved.scheduled_departure)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Flight', text });
        return;
      } catch {
        // user cancelled or share unsupported; fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable; nothing more we can do
    }
  }

  function handleAddToCalendar() {
    const ics = buildIcs(saved);
    const blob = new Blob([ics], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${saved.ident}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const phaseLabel: Record<string, string> = {
    upcoming: 'Scheduled',
    approaching: 'Departing soon',
    active: 'In progress',
    completed: 'Completed',
  };

  return (
    <div className="card">
      <div className="place-card-header">
        <div>
          <h3 className="place-card-name">{saved.ident}</h3>
          <p className="place-card-meta">
            {saved.origin} → {saved.destination}
          </p>
        </div>
        {status && <span className="badge badge-neutral">{phaseLabel[status.phase] ?? status.phase}</span>}
      </div>

      {!flight && <div className="skeleton" style={{ height: 80, marginTop: 12 }} />}

      {flight && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 14 }}>
            <strong>Scheduled:</strong> {formatTimeLabel(flight.scheduled_out)} → {formatTimeLabel(flight.scheduled_in)}
          </p>
          {(flight.estimated_out || flight.estimated_in) && (
            <p style={{ fontSize: 14 }}>
              <strong>Estimated:</strong> {formatTimeLabel(flight.estimated_out)} → {formatTimeLabel(flight.estimated_in)}
            </p>
          )}
          {flight.gate_origin && (
            <p style={{ fontSize: 14 }}>
              <strong>Gate:</strong> {flight.gate_origin} {flight.terminal_origin ? `(Terminal ${flight.terminal_origin})` : ''}
            </p>
          )}
          {flight.aircraft_type && <p style={{ fontSize: 14 }}><strong>Aircraft:</strong> {flight.aircraft_type}</p>}
          {flight.status && <p style={{ fontSize: 14 }}><strong>Status:</strong> {flight.status}</p>}
          {flight.cancelled && <p className="error-text">This flight has been cancelled.</p>}
          {flight.diverted && <p className="error-text">This flight has been diverted.</p>}
        </div>
      )}

      <div className="action-row">
        <a
          className="action-btn"
          href={`https://flightaware.com/live/flight/${encodeURIComponent(saved.ident)}`}
          target="_blank"
          rel="noreferrer"
        >
          <PlaneStatusIcon />
          Track Flight
        </a>
        <button type="button" className="action-btn" onClick={handleShare}>
          {copied ? 'Copied!' : 'Share'}
        </button>
        <button type="button" className="action-btn" onClick={handleAddToCalendar}>
          Add to Calendar
        </button>
      </div>

      <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 12 }} onClick={onRemove}>
        Remove Saved Flight
      </button>
    </div>
  );
}
